// src/lib/riskMetrics.js
// 현재 보유비중(고정) 기반 사전(ex-ante) 포트폴리오 리스크 지표.
// 각 종목의 로컬통화 일간수익률에 현 부호비중(롱+/숏−, Σ|w|=1)을 곱해
// 포트폴리오 수익률 시계열을 만들고 변동성·Sharpe·IR·베타를 산출.
// (환율 변동은 제외 — 자산선택 위험 기준. 거래일 교집합 사용.)

const dstr = (ts) => new Date(ts * 1000).toISOString().slice(0, 10);

// points: [[ts, close], ...] → Map(date -> 일간수익률)
function toReturnMap(points) {
  const m = new Map();
  const s = [...points].sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < s.length; i++) {
    const p0 = s[i - 1][1], p1 = s[i][1];
    if (p0 > 0 && p1 > 0) m.set(dstr(s[i][0]), p1 / p0 - 1);
  }
  return m;
}

const mean = (a) => a.reduce((s, x) => s + x, 0) / a.length;
function std(a) {
  if (a.length < 2) return 0;
  const mu = mean(a);
  return Math.sqrt(a.reduce((s, x) => s + (x - mu) ** 2, 0) / (a.length - 1));
}
function cov(a, b) {
  const n = a.length; if (n < 2) return 0;
  const ma = mean(a), mb = mean(b);
  let s = 0; for (let i = 0; i < n; i++) s += (a[i] - ma) * (b[i] - mb);
  return s / (n - 1);
}

// 롱 바스켓 vs 숏 바스켓 일간수익률의 상관.
//   longWeights/shortWeights: [{ ticker, w }] — 각 사이드 내 양(+)의 비중(합=1).
//   series: {ticker:{points}}. 두 바스켓 모두에 걸친 공통 거래일 교집합으로 계산.
// 해석: 높을수록 숏이 롱을 잘 헤지(시장 하락 시 동반 하락) → 시장중립적.
export function computeLongShortCorr({ longWeights, shortWeights, series }) {
  const build = (ws) => {
    const used = [];
    for (const { ticker, w } of ws) {
      const s = series?.[ticker];
      if (!s?.points || s.points.length < 2 || !w) continue;
      used.push({ ticker, w, map: toReturnMap(s.points) });
    }
    return used;
  };
  const L = build(longWeights || []), S = build(shortWeights || []);
  if (!L.length || !S.length) return { insufficient: true, coveredLong: L.length, coveredShort: S.length };

  let common = null;
  for (const { map } of [...L, ...S]) {
    const dates = new Set(map.keys());
    common = common ? new Set([...common].filter((d) => dates.has(d))) : dates;
  }
  const dates = [...(common || [])].sort();
  if (dates.length < 20) return { insufficient: true, n: dates.length, coveredLong: L.length, coveredShort: S.length };

  const wsum = (arr) => arr.reduce((s, x) => s + x.w, 0) || 1;
  const sumL = wsum(L), sumS = wsum(S);
  const longR = [], shortR = [];
  for (const d of dates) {
    let rl = 0, rs = 0;
    for (const { w, map } of L) rl += (w / sumL) * map.get(d);
    for (const { w, map } of S) rs += (w / sumS) * map.get(d);
    longR.push(rl); shortR.push(rs);
  }
  const sl = std(longR), ss = std(shortR);
  const corr = sl > 0 && ss > 0 ? cov(longR, shortR) / (sl * ss) : null;
  return { corr, n: dates.length, coveredLong: L.length, coveredShort: S.length };
}

// weights: [{ ticker, w }] (w 부호포함), series: {ticker:{points}}, benchSeries: {points}|null
export function computeRiskMetrics({ weights, series, benchSeries, riskFree = 0.03, ppy = 252 }) {
  const retMaps = {}, used = [];
  for (const { ticker, w } of weights) {
    const s = series?.[ticker];
    if (!s || !s.points || s.points.length < 2 || !w) continue;
    retMaps[ticker] = toReturnMap(s.points);
    used.push({ ticker, w });
  }
  if (!used.length) return null;

  // 공통 거래일(교집합)
  let common = null;
  for (const { ticker } of used) {
    const dates = new Set(retMaps[ticker].keys());
    common = common ? new Set([...common].filter((d) => dates.has(d))) : dates;
  }
  const dates = [...(common || [])].sort();
  if (dates.length < 20) return { insufficient: true, n: dates.length, covered: used.length };

  const benchMap = benchSeries?.points ? toReturnMap(benchSeries.points) : null;
  const port = [], benchAligned = [];
  for (const d of dates) {
    let r = 0;
    for (const { ticker, w } of used) r += w * retMaps[ticker].get(d);
    port.push(r);
    benchAligned.push(benchMap && benchMap.has(d) ? benchMap.get(d) : null);
  }

  const vol = std(port) * Math.sqrt(ppy);
  const annRet = mean(port) * ppy;
  const sharpe = vol > 0 ? (annRet - riskFree) / vol : null;
  const irCash = vol > 0 ? annRet / vol : null; // 벤치=현금(0) 정보비율

  let irBench = null, beta = null, corr = null, te = null, benchDays = 0;
  const pP = [], pB = [];
  for (let i = 0; i < dates.length; i++) if (benchAligned[i] != null) { pP.push(port[i]); pB.push(benchAligned[i]); }
  benchDays = pP.length;
  if (benchDays >= 20) {
    const active = pP.map((x, i) => x - pB[i]);
    te = std(active) * Math.sqrt(ppy);
    irBench = te > 0 ? (mean(active) * ppy) / te : null;
    const vb = cov(pB, pB);
    beta = vb > 0 ? cov(pP, pB) / vb : null;
    const sp = std(pP), sb = std(pB);
    corr = sp > 0 && sb > 0 ? cov(pP, pB) / (sp * sb) : null;
  }

  return {
    vol, annRet, sharpe, irCash, irBench, beta, corr, te,
    n: dates.length, benchDays, covered: used.length,
    start: dates[0], end: dates[dates.length - 1],
  };
}
